import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Tooltip as HeroTooltip,
  TooltipProps as HeroTooltipProps,
} from '@heroui/react';

export interface TooltipProps extends HeroTooltipProps {
  content: ReactNode;
  children: ReactNode;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end';
  showArrow?: boolean;
  offset?: number;
  delay?: number;
}

const useHoverDelay = (delay: number) => {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => clear, []);

  const onMouseEnter = () => {
    clear();
    timerRef.current = setTimeout(() => setIsOpen(true), delay);
  };

  const onMouseLeave = () => {
    clear();
    timerRef.current = setTimeout(() => setIsOpen(false), 50);
  };

  return { isOpen, onMouseEnter, onMouseLeave };
};

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  showArrow = true,
  offset = 5,
  delay = 500,
  ...props
}) => {
  const { isOpen, onMouseEnter, onMouseLeave } = useHoverDelay(delay);

  if (!content) return <>{children}</>;

  return (
    <HeroTooltip
      placement={placement}
      showArrow={showArrow}
      offset={offset}
      isOpen={isOpen}
      content={<div className="max-w-md">{content}</div>}
      color="foreground"
      className="p-2"
      {...props}
    >
      <span onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {children}
      </span>
    </HeroTooltip>
  );
};

export default Tooltip;
